<template>
  <div class="instructors-page">
    <Sidebar 
      @toggle-sidebar="handleSidebarToggle" 
      @sidebar-collapse="handleSidebarCollapse" 
    />
    
    <main 
      class="instructors-main" 
      :class="{ 
        'sidebar-open': isSidebarOpen, 
        'sidebar-collapsed': isSidebarCollapsed 
      }"
    >
      <!-- Dashboard header with stats -->
      <section class="dashboard-header">
        <PageHeader 
          :total-instructors="totalInstructors" 
          :total-courses="totalCourses" 
        />
      </section>

      <!-- Search and filters bar -->
      <section class="filters-container">
        <FiltersSection 
          :search-query="searchQuery"
          :selected-expertise="selectedExpertise"
          :selected-rating="selectedRating"
          :sort-by="sortBy"
          :has-active-filters="hasActiveFilters"
          :expertise-options="expertiseOptions"
          :selected-language="''"
          :selected-availability="''"
          :selected-price="''"
          :selected-experience="''"
          :selected-teaching-style="''"
          :selected-location="''"
          @clear-search="clearSearch"
          @clear-filters="clearFilters"
          @update:search-query="searchQuery = $event"
          @update:selected-expertise="selectedExpertise = $event"
          @update:selected-rating="selectedRating = $event"
          @update:sort-by="sortBy = $event"
        />
      </section>

      <!-- Status states -->
      <section class="status-container">
        <transition name="fade" mode="out-in">
          <LoadingState v-if="loading" key="loading" />
          <ErrorState 
            v-else-if="error" 
            :error="error" 
            @retry="fetchInstructors" 
            key="error" 
          />
          <NoResults v-else-if="filteredInstructors.length === 0" key="no-results" />
        </transition>
      </section>

      <!-- Instructors grid -->
      <transition name="fade" mode="out-in">
        <section v-if="!loading && !error && filteredInstructors.length > 0" class="instructors-content">
          <div class="instructors-grid">
            <InstructorCard 
              v-for="instructor in sortedAndFilteredInstructors" 
              :key="instructor.id" 
              :instructor="instructor"
              @view-profile="viewInstructorDetails"
            />
          </div>
        </section>
      </transition>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';
import PageHeader from './instructors/PageHeader.vue';
import FiltersSection from './instructors/FiltersSection.vue';
import LoadingState from './instructors/LoadingState.vue';
import ErrorState from './instructors/ErrorState.vue';
import NoResults from './instructors/NoResults.vue';
import InstructorCard from './instructors/InstructorCard.vue';

export default {
  name: 'InstructorsList',
  components: {
    Sidebar,
    PageHeader,
    FiltersSection,
    LoadingState,
    ErrorState,
    NoResults,
    InstructorCard
  },
  data() {
    return {
      instructors: [],
      loading: true,
      error: null,
      isSidebarOpen: false,
      isSidebarCollapsed: false,
      searchQuery: '',
      selectedExpertise: '',
      selectedRating: '',
      sortBy: 'name-asc'
    };
  },
  computed: {
    filteredInstructors() {
      if (!Array.isArray(this.instructors)) {
        return [];
      }
      
      return this.instructors.filter(instructor => {
        // Handle null/undefined instructor
        if (!instructor) return false;

        const fullName = `${instructor.first_name || ''} ${instructor.last_name || ''}`.toLowerCase();
        const expertise = (instructor.expertise || '').toLowerCase();
        const searchTerm = this.searchQuery.toLowerCase();
        
        // Search query filter
        const matchesSearch = !this.searchQuery || 
          fullName.includes(searchTerm) ||
          expertise.includes(searchTerm);

        // Expertise filter
        const matchesExpertise = !this.selectedExpertise ||
          expertise.includes(this.selectedExpertise.toLowerCase());

        // Rating filter
        const instructorRating = parseFloat(instructor.rating || 0);
        const filterRating = this.selectedRating ? parseFloat(this.selectedRating) : 0;
        const matchesRating = !this.selectedRating || instructorRating >= filterRating;

        return matchesSearch && matchesExpertise && matchesRating;
      });
    },
    totalInstructors() {
      return this.filteredInstructors.length;
    },
    totalCourses() {
      return this.filteredInstructors.reduce((total, instructor) => 
        total + (instructor.course_count || 0), 0);
    },
    expertiseOptions() {
      if (!Array.isArray(this.instructors)) return [];
      
      const expertiseSet = new Set();
      this.instructors.forEach(instructor => {
        if (instructor?.expertise) {
          instructor.expertise.split(',').forEach(exp => {
            expertiseSet.add(exp.trim());
          });
        }
      });
      return Array.from(expertiseSet).sort();
    },
    hasActiveFilters() {
      return this.searchQuery || this.selectedExpertise || this.selectedRating;
    },
    sortedAndFilteredInstructors() {
      const result = [...this.filteredInstructors];
      const [field, direction] = this.sortBy.split('-');
      
      return result.sort((a, b) => {
        let comparison = 0;
        let nameA, nameB, ratingA, ratingB, coursesA, coursesB;
        
        switch (field) {
          case 'name':
            nameA = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
            nameB = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
            comparison = nameA.localeCompare(nameB);
            break;
          case 'rating':
            ratingA = parseFloat(a.rating || 0);
            ratingB = parseFloat(b.rating || 0);
            comparison = ratingA - ratingB;
            break;
          case 'courses':
            coursesA = parseInt(a.course_count || 0);
            coursesB = parseInt(b.course_count || 0);
            comparison = coursesA - coursesB;
            break;
        }
        
        return direction === 'asc' ? comparison : -comparison;
      });
    }
  },
  methods: {
    handleSidebarToggle(isOpen) {
      this.isSidebarOpen = isOpen;
    },
    handleSidebarCollapse(isCollapsed) {
      this.isSidebarCollapsed = isCollapsed;
    },
    clearSearch() {
      this.searchQuery = '';
    },
    viewInstructorDetails(instructorId) {
      this.$router.push(`/instructors/${instructorId}`);
    },
    async fetchInstructors() {
      this.loading = true;
      this.error = null;
      
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('No authentication token found');
        }

        const response = await axios.get('https://api.kewaar.com/api/admin/courses/instructors/all?per_page=100', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data.success) {
          this.instructors = Array.isArray(response.data.data.data) 
            ? response.data.data.data 
            : [];
        } else {
          throw new Error(response.data.message || 'Failed to fetch instructors');
        }
      } catch (error) {
        console.error('Error fetching instructors:', error);
        this.error = error.response?.data?.message || error.message || 'An unexpected error occurred';
        this.instructors = [];
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.searchQuery = '';
      this.selectedExpertise = '';
      this.selectedRating = '';
      this.sortBy = 'name-asc';
    }
  },
  async created() {
    await this.fetchInstructors();
  }
};
</script>

<style scoped>
/* Main layout */
.instructors-page {
  display: flex;
  min-height: 100vh;
  background-color: var(--bg-light);
  position: relative;
}

.instructors-main {
  flex: 1;
  transition: all 0.3s ease;
  margin-left: 280px;
  padding: 2rem;
  background: linear-gradient(135deg, var(--bg-light) 0%, var(--bg-lighter) 100%);
}

.instructors-main.sidebar-open {
  margin-left: 280px;
}

.instructors-main.sidebar-collapsed {
  margin-left: 80px;
}

/* Section containers */
.dashboard-header,
.filters-container,
.status-container,
.instructors-content {
  margin-bottom: 2rem;
  backdrop-filter: blur(10px);
  border-radius: 12px;
}

.instructors-content {
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.05);
  padding: 1.5rem;
}

.instructors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

/* Animations */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* Responsive adjustments */
@media (max-width: 1400px) {
  .instructors-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 1200px) {
  .instructors-main {
    margin-left: 0;
    padding: 1.5rem;
  }
  
  .instructors-main.sidebar-open {
    margin-left: 280px;
  }
}

@media (max-width: 992px) {
  .instructors-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
  }
  
  .instructors-content {
    padding: 1.25rem;
  }
}

@media (max-width: 768px) {
  .instructors-main {
    padding: 1rem;
  }
  
  .instructors-grid {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 1rem;
  }
  
  .instructors-content {
    padding: 1rem;
  }
}

@media (max-width: 576px) {
  .instructors-grid {
    grid-template-columns: 1fr;
  }
}
</style> 